import { useEffect, useMemo, useState } from "react";
import App from "../../App.jsx";
import { DemoContext } from "../../lib/DemoContext.jsx";
import { createDemoServices } from "./services.js";
import { usePreferences } from "../preferences/usePreferences";
import { useI18n } from "../i18n/I18nProvider";
import { Settings } from "../settings/Settings";
import { ThemeToggle } from "../workspace/ThemeToggle";
import { Ic } from "../../components/Icons.jsx";
import type { Api, Usage } from "../types";
import "./demo.css";

export function OriginalDemo({ api, logout }: { api: Api; logout: () => Promise<void> }) {
  const { profile, theme } = usePreferences();
  const { t, locale } = useI18n();
  const [ready, setReady] = useState(false);
  const [historyGranted, setHistoryGranted] = useState(true);
  const [settings, setSettings] = useState(false), [menu, setMenu] = useState(false);
  const [usage, setUsage] = useState<Usage | null>(null), [error, setError] = useState("");
  const services = useMemo(() => createDemoServices(api), [api]);
  useEffect(() => () => services.dispose(), [services]);
  async function load() {
    try {
      const consent = await api.request<{items: {kind: string; granted: boolean}[]}>("/consents");
      setHistoryGranted(consent.items.some(item => item.kind === "history" && item.granted));
      setReady(true); setError("");
    } catch (error) { setError((error as Error).message); }
  }
  useEffect(() => { void load(); }, [api]);
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menu]);
  useEffect(() => {
    if (settings) api.request<Usage>("/usage").then(setUsage).catch(e => setError(e.message));
  }, [settings, api]);
  if (error && !ready) return <div className="product lyncia-product"><p role="alert">{error}</p><button onClick={() => void load()}>{t("重试", "Retry")}</button><button onClick={() => void logout()}>{t("退出登录", "Sign out")}</button></div>;
  if (!ready) return <div role="status">{t("正在加载…", "Loading…")}</div>;
  const username = profile?.email || t("账号", "Account");
  const footer = <div className="demo-account">
    <ThemeToggle />
    <div className="sb-user" onClick={e => e.stopPropagation()}>
      <button className="sb-user-btn" aria-label={t("账号菜单", "Account menu")} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
        <span className="avatar">{username.slice(0, 2)}</span><span className="name demo-account-identity"><span>{username}</span></span><Ic.Chev size={16} />
      </button>
      {menu && <div className="menu sb-user-menu" role="menu"><button className="menu-item" role="menuitem" onClick={() => { window.location.hash = "api-console"; }}>{t("API 控制台", "API Console")}</button>{profile?.roles?.includes("clinician") && <button className="menu-item" role="menuitem" onClick={() => { window.location.hash = "clinician"; }}>{t("咨询师工作台", "Clinician workspace")}</button>}{profile?.roles?.includes("admin") && <button className="menu-item" role="menuitem" onClick={() => { window.location.hash = "admin"; }}>{t("管理后台", "Admin console")}</button>}<button className="menu-item" role="menuitem" onClick={() => { setMenu(false); setSettings(true); }}>{t("设置", "Settings")}</button></div>}
    </div>
  </div>;
  return <DemoContext.Provider value={services as any}>
    <div className="original-demo">
      <App accountId={profile?.id || ''} accountFooter={footer} accountTheme={theme} locale={locale}
        thinkingDescription={t("Thinking：开启后在文字面板显示思考过程；语音只朗读回复正文", "Thinking: show reasoning in the text panel; voice reads only the reply.")}
        accountNotice={!historyGranted && <div className="demo-consent-notice" role="status">{t("对话和课程需要保存历史。请在设置中开启后继续。", "Enable conversation history in Settings to chat and resume courses.")} <button onClick={() => setSettings(true)}>{t("打开设置", "Open settings")}</button></div>} />
      {settings && <div className="demo-settings-overlay product lyncia-product" role="dialog" aria-modal="true" aria-label={t("设置", "Settings")}>
        <Settings api={api} profile={profile!} usage={usage} logout={logout}
          onHistoryChange={setHistoryGranted} onClose={() => setSettings(false)} />
      </div>}
    </div>
  </DemoContext.Provider>;
}
